import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { apiEndpoints } from "../utils/api";

interface EffectiveFeaturesResponse {
  tier: string;
  features: string[];
}

interface AuthenticatedApi {
  get: <T = any>(endpoint: string, options?: RequestInit) => Promise<T>;
}

interface FeatureStore {
  tier: string | null;
  features: string[];
  loading: boolean;
  error: string | null;
  lastFetchedAt: number | null;

  has: (key: string) => boolean;
  load: (api: AuthenticatedApi) => Promise<void>;
  refresh: (api: AuthenticatedApi) => Promise<void>;
  reset: () => void;
}

export const useFeatureStore = create<FeatureStore>()(
  devtools(
    (set, get) => ({
      tier: null,
      features: [],
      loading: false,
      error: null,
      lastFetchedAt: null,

      has: (key: string) => get().features.includes(key),

      load: async (api: AuthenticatedApi) => {
        const state = get();
        if (!api || state.loading) return;

        set({ loading: true, error: null });
        try {
          const resp = await api.get<EffectiveFeaturesResponse>(
            apiEndpoints.features.effective,
          );
          set({
            tier: resp?.tier ?? "creator",
            features: Array.isArray(resp?.features) ? resp.features : [],
            loading: false,
            error: null,
            lastFetchedAt: Date.now(),
          });
        } catch (e: any) {
          set({
            loading: false,
            error: e?.message || "Failed to load features",
          });
        }
      },

      refresh: async (api: AuthenticatedApi) => {
        await get().load(api);
      },

      reset: () => {
        set({
          tier: null,
          features: [],
          loading: false,
          error: null,
          lastFetchedAt: null,
        });
      },
    }),
    { name: "feature-store" },
  ),
);
